// react
import { useMemo, useRef } from 'react';
import {
  RouterProvider,
  createBrowserRouter,
  Navigate,
  Outlet,
} from 'react-router-dom';

// context
import { AccountRole, useTenant, Permission, Feature } from 'context';

// models
import { isIFAOrganizationTypeID } from '@karehero/models';

// router
import {
  RootLayout,
  useRouterGuard,
  ROUTES,
  DYNAMIC_ROUTES,
  wildcard,
} from 'router';

// views
import {
  CannotBookCall,
  Root,
  RootChrome,
  RootAdmin,
  RootOrganizationOwner,
  RootPartnerAdmin,
  PartnerAdminDashboard,
  SignIn,
  Authenticate,
  Dashboard,
  DeleteAccount,
  CareConciergeReferral,
  CareConciergeReferralEmailConfirmation,
  WelcomeActions,
  ManageCareCircles,
  ManageAccounts,
  NewCareCircle,
  ManageInvitedMembers,
  ServiceAccountTable,
  Settings,
  Organiser,
  MemberActions,
  ActionPlan,
  ActionPlanCategory,
  ActionPlanContent,
  ActionPlanTable,
  CareCircle,
  CareCircleMemberInfo,
  ArticleBookmarks,
  ArticleOverview,
  ArticleContent,
  SJPResources,
  ServiceOverview,
  Service,
  WebinarOverview,
  WebinarContent,
  OnboardingMemberCreate,
  OnboardingMemberCreateV1,
  CareProfile,
  CareProfileStart,
  CareProfileForm,
  CareProfileComplete,
  CarePlan,
  KeyInformation,
  CareSearchShortlists,
  CareSearch,
  ShortlistDetail,
  Directory,
  OnboardingDirectoryCareHome,
  OnboardingDirectoryAtHome,
  BookCall,
  BookCallDetails,
  PoaSuccess,
  EnterprisePortal,
  IFAPortal,
  IFADashboardPortal,
  SelfReferral,
  SelfReferralSent,
  AdminInvite,
  AdminInviteSent,
  ClientReferralStart,
  ClientReferralLeadData,
  ClientReferralReview,
  ClientReferralSent,
  OrganizationEditor,
  OrganizationTable,
  OrganizationMemberTable,
  ReferringPartnerEditor,
  ReferringPartnerTable,
  PartnerProductTable,
  AttendanceAllowance,
  AttendanceAllowanceForm,
  AttendanceAllowanceComplete,
  AttendanceAllowanceSuccess,
  AdminCareSearch,
  Poa,
  FinancialStatusForm,
  FinancialStatusStart,
  FinancialStatusComplete,
  DirectoryProfileView,
  TypeformSubmitted,
  TermsOfService,
  VerifyAccess,
  RootVerify,
  EnterInviteCode,
  VerificationSuccess,
  AccountCreated,
  MagicLinkRedirect,
  VerifyAccount,
  CareCircleCreate,
  AdminCareCircleCreate,
  AdminCareCircleView,
  AvatarEditor,
  CareCircleInvite,
  KeyDocuments,
  DocumentUpload,
  AudioRecord,
  CallNoteEditor,
  CallNoteView,
  CallNotesList,
  AdminCallNotes,
  AdminCaseloadManager,
  CallsList,
  CallSummary,
  Notifications,
} from 'views';
import LoadingPage from '@karehero/llama/components/pages/Loading/Loading';
import { PartnerRegister } from 'views/PartnerRegister';
import { SignOut } from 'views/SignOut';
import { BenefitsEligibilityStart } from 'views/BenefitsEligibilityStart';
import { BenefitsEligibilityForm } from 'views/BenefitsEligibilityForm';
import { BenefitsEligibilityComplete } from 'views/BenefitsEligibilityComplete';
import { BenefitsEligibilityV2Start } from 'views/BenefitsEligibilityV2Start';
import { BenefitsEligibilityV2Form } from 'views/BenefitsEligibilityV2Form';
import { Sandbox } from 'views/Sandbox';
import { ServerChat } from 'views/ServerChat';
import { BenefitsEligibilityV2Complete } from 'views/BenefitsEligibilityV2Complete';
import { OnboardingMemberCreateV4 } from 'views/OnboardingMemberCreateV4';
import { AdminBacktests } from 'views/AdminBacktests';
import { AdminBacktestNew } from 'views/AdminBacktestNew';
import { AdminBacktest } from 'views/AdminBacktest';
import { useOnboardingRoute } from 'hooks/useOnboardingRoute';

// TODO[rotuer]: remove all gates in favour of assigning only one route per
// view component and using loader guards.
const BookCallGate = () => {
  const { tenant } = useTenant();
  return tenant.permissions.includes(Permission.BOOK_CALL) ? (
    <BookCall />
  ) : (
    <CannotBookCall />
  );
};

const CareSearchGate = () => {
  const { tenant } = useTenant();

  if (tenant.features.includes(Feature.SHORTLISTS_V2)) {
    return <CareSearchShortlists />;
  }

  return tenant.accountRole === AccountRole.ADMIN ? (
    <AdminCareSearch />
  ) : (
    <CareSearch />
  );
};

const InvitePortalGate = () => {
  const { tenant } = useTenant();
  const isIFA = isIFAOrganizationTypeID(tenant.organizationTypeId);

  if (isIFA) {
    if (tenant.features.includes(Feature.IFA_PORTAL)) {
      return <IFADashboardPortal />;
    } else {
      return <IFAPortal />;
    }
  }
  return <EnterprisePortal />;
};

const FeatureGate = ({
  feature,
  fallbackRedirect: fallback,
}: {
  feature: Feature;
  fallbackRedirect?: string;
}) => {
  const { tenant } = useTenant();
  if (!tenant.featuresReady) return <LoadingPage />;
  if (!tenant.features.includes(feature)) {
    return <Navigate to={fallback ?? '/'} replace />;
  }
  return <Outlet />;
};

export const EligibilityCalculatorGate = ({
  version,
}: {
  version: 'v1' | 'v2';
}) => {
  const routeVersions: Record<string, string> = {
    v1: ROUTES.ELIGIBILITY_CALCULATOR,
    v2: ROUTES.ELIGIBILITY_CALCULATOR_V2,
  };
  const { tenant } = useTenant();
  if (!tenant.featuresReady) return <LoadingPage />;

  const active = tenant.activeFeatureVersions.benefitsCalculator;

  if (!active) return <Navigate to='/' replace />;

  if (active !== version) {
    return <Navigate to={routeVersions[active]} replace />;
  }

  return <Outlet />;
};

const PermissionGate = ({ permission }: { permission: Permission }) => {
  const { tenant } = useTenant();
  if (!tenant.featuresReady) return <LoadingPage />;
  if (!tenant.permissions.includes(permission)) {
    return <Navigate to='/' replace />;
  }
  return <Outlet />;
};

export const IndexGate = () => {
  const { tenant } = useTenant();
  const onboarding = useOnboardingRoute();

  if (!tenant.session.isAuthenticated) {
    return <Navigate to={ROUTES.SIGN_IN} replace />;
  }
  if (tenant.session.isNewKindeAccount) {
    return <Navigate to={ROUTES.ACCOUNT_CREATED} replace />;
  }
  if (tenant.accountRole === AccountRole.PARTNER_ADMIN) {
    return <Navigate to={ROUTES.PARTNER_ADMIN} replace />;
  }
  if (!tenant.session.isVerified) {
    if (tenant.session.isReferringPartner) {
      return <Navigate to={ROUTES.PARTNER_REGISTER} replace />;
    }
    return <Navigate to={ROUTES.VERIFY_ACCESS} replace />;
  }
  if (tenant.accountRole === AccountRole.ORG_OWNER) {
    return <Navigate to={ROUTES.PORTAL} replace />;
  }
  if (!tenant.session.isOnboarded) {
    // Which onboarding is flagged, so wait for the flags.
    if (!onboarding.isReady) return <LoadingPage />;
    return <Navigate to={onboarding.value} replace />;
  }
  return <Outlet />;
};

export const useRouter = () => {
  const {
    requireAuth,
    requireOnboarded,
    requireRoles,
    requireVerified,
    requireReferringPartner,
    buildLoader,
  } = useRouterGuard();

  const requireNoAuthOrUnverified = useMemo(
    () => requireAuth(false) || requireVerified(false),
    [requireAuth, requireVerified],
  );

  /**
   * Recursively prune route branches whose loader is tagged with _allow: false.
   * This lets the same path exist under multiple role-guarded layouts — only
   * the branch matching the current tenant's role survives, avoiding React
   * Router's first-match conflict.
   */
  const pruneRoutes = (routes: any[]): any[] =>
    routes
      .filter((route) => (route.loader as any)?._allow !== false)
      .map((route) =>
        route.children
          ? { ...route, children: pruneRoutes(route.children) }
          : route,
      );

  const routes = useMemo(
    () => [
      {
        path: ROUTES.HOME,
        element: <RootLayout />,
        children: [
          {
            path: ROUTES.TERMS_OF_SERVICE,
            element: <TermsOfService />,
          },
          {
            path: wildcard(ROUTES.AUTHENTICATE),
            element: <Authenticate />,
          },
          {
            path: ROUTES.SIGN_OUT,
            element: <SignOut />,
          },
          {
            path: ROUTES.DELETE_ACCOUNT,
            element: <DeleteAccount />,
          },
          {
            path: ROUTES.TYPEFORM_SUBMITTED,
            element: <TypeformSubmitted />,
          },

          // requires no auth
          {
            loader: buildLoader(requireAuth(false)),
            children: [
              {
                path: wildcard(ROUTES.SIGN_IN),
                element: <SignIn />,
              },
              {
                path: ROUTES.REGISTER,
                element: <MagicLinkRedirect />,
              },
              {
                path: ROUTES.ONBOARDING_REDIRECT,
                element: <MagicLinkRedirect />,
              },
            ],
          },

          // requires no auth or unverified org
          {
            loader: buildLoader(requireNoAuthOrUnverified),
            children: [
              {
                path: DYNAMIC_ROUTES.REGISTER_ORGANIZATION(':organizationID'),
                element: <SignIn />,
              },
              {
                path: ROUTES.REQUEST_INVITE,
                element: <SignIn />,
              },
              {
                path: ROUTES.REGISTER_INVITE_CODE,
                element: <SignIn />,
              },
            ],
          },

          // external referral form
          {
            loader: buildLoader(requireNoAuthOrUnverified),
            element: <FeatureGate feature={Feature.EXTERNAL_REFERRAL_FORM} />,
            children: [
              {
                path: ROUTES.REFERRAL,
                element: <CareConciergeReferral />,
              },
              {
                path: ROUTES.REFERRAL_CONFIRM_EMAIL,
                element: <CareConciergeReferralEmailConfirmation />,
              },
            ],
          },

          // referring partner (via email domain or OCC)
          {
            loader: buildLoader(requireAuth(true) && requireVerified(false)),
            children: [
              {
                path: 'partner/register',
                element: <PartnerRegister />,
              },
            ],
          },

          // unverified member
          {
            loader: buildLoader(
              requireAuth(true) &&
                requireVerified(false) &&
                requireReferringPartner(false),
            ),
            children: [
              {
                path: ROUTES.VERIFY,
                children: [
                  {
                    index: true,
                    element: <VerifyAccess />,
                  },
                  {
                    element: <RootVerify />,
                    children: [
                      {
                        path: ROUTES.VERIFY_ACCESS,
                        element: <VerifyAccess />,
                      },
                      {
                        handle: { stack: true },
                        children: [
                          {
                            path: ROUTES.VERIFY_ENTER_CONFIRMATION,
                            element: <EnterInviteCode />,
                          },
                        ],
                      },
                    ],
                  },
                ],
              },
            ],
          },

          // post verification success
          {
            path: ROUTES.VERIFY_SUCCESS,
            loader: buildLoader(requireAuth(true)),
            element: <VerificationSuccess />,
          },

          // does not require unverified as they may have been autoverified
          {
            path: ROUTES.ACCOUNT_CREATED,
            element: <AccountCreated />,
          },

          // onboarding
          {
            loader: buildLoader(
              requireAuth(true) &&
                requireOnboarded(false) &&
                requireVerified(true),
            ),
            children: [
              {
                path: ROUTES.REGISTER_MEMBER,
                element: <OnboardingMemberCreateV1 />,
              },
              {
                path: ROUTES.ONBOARDING,
                element: <OnboardingMemberCreate />,
              },
            ],
          },

          // onboarding v4 (conversational)
          {
            loader: buildLoader(requireAuth(true)),
            children: [
              {
                path: ROUTES.ONBOARDING_V4,
                element: <OnboardingMemberCreateV4 />,
              },
            ],
          },

          // include navbar
          {
            element: <Root />,
            children: [
              // shared routes (accessible to all authenticated roles)
              // rootStack renders as a full-screen overlay animated by Root
              {
                handle: { rootStack: true },
                children: [
                  {
                    path: ROUTES.SETTINGS,
                    element: <Settings />,
                  },
                  {
                    path: ROUTES.AVATAR_EDITOR,
                    element: <AvatarEditor />,
                  },
                  {
                    path: ROUTES.NOTIFICATIONS,
                    element: <Notifications />,
                  },
                ],
              },

              // partner admin only (outside Root — no care circle, chat, or event stream)
              {
                path: ROUTES.PARTNER_ADMIN,
                element: <RootPartnerAdmin />,
                handle: { layout: 'portal' },
                loader: buildLoader(
                  requireAuth(true) && requireRoles(AccountRole.PARTNER_ADMIN),
                ),
                children: [
                  {
                    index: true,
                    element: <PartnerAdminDashboard />,
                  },
                ],
              },

              // organization owner only
              {
                element: <RootOrganizationOwner />,
                handle: { layout: 'portal' },
                loader: buildLoader(requireRoles(AccountRole.ORG_OWNER)),
                children: [
                  {
                    path: ROUTES.PORTAL,
                    element: <InvitePortalGate />,
                  },
                  {
                    handle: { stack: true },
                    children: [
                      {
                        path: ROUTES.PORTAL_SELF_REFERRAL,
                        element: <SelfReferral />,
                      },
                      {
                        path: ROUTES.PORTAL_SELF_REFERRAL_SENT,
                        element: <SelfReferralSent />,
                      },
                      {
                        path: ROUTES.PORTAL_ADMIN_INVITE,
                        element: <AdminInvite />,
                      },
                      {
                        path: ROUTES.PORTAL_ADMIN_INVITE_SENT,
                        element: <AdminInviteSent />,
                      },
                      {
                        path: ROUTES.PORTAL_CLIENT_REFERRAL,
                        element: <ClientReferralStart />,
                      },
                      {
                        path: ROUTES.PORTAL_CLIENT_REFERRAL_LEAD_DATA,
                        element: <ClientReferralLeadData />,
                      },
                      {
                        path: ROUTES.PORTAL_CLIENT_REFERRAL_REVIEW,
                        element: <ClientReferralReview />,
                      },
                      {
                        path: ROUTES.PORTAL_CLIENT_REFERRAL_SENT,
                        element: <ClientReferralSent />,
                      },
                      {
                        path: DYNAMIC_ROUTES.LIBRARY_ARTICLE(':articleId'),
                        element: <ArticleContent />,
                      },
                    ],
                  },
                  {
                    path: ROUTES.RESOURCES,
                    element: <SJPResources />,
                  },
                ],
              },

              // admin only
              {
                path: ROUTES.ADMIN,
                element: <RootAdmin />,
                handle: { layout: 'admin' },
                loader: buildLoader(requireRoles(AccountRole.ADMIN)),
                children: [
                  {
                    index: true,
                    element: <WelcomeActions />,
                  },
                  {
                    path: ROUTES.ADMIN_ACTION_PLAN,
                    element: <ActionPlanTable />,
                  },
                  {
                    path: ROUTES.ADMIN_CARE_CIRCLE_MANAGE,
                    element: <ManageCareCircles />,
                  },
                  {
                    path: ROUTES.ADMIN_CASELOAD,
                    element: <AdminCaseloadManager />,
                  },
                  {
                    path: DYNAMIC_ROUTES.ADMIN_CASELOAD_FOR(':accountId'),
                    element: <AdminCaseloadManager />,
                  },
                  {
                    path: DYNAMIC_ROUTES.ADMIN_CARE_CIRCLE_NEW(':accountID'),
                    element: <AdminCareCircleCreate />,
                    handle: { stack: true },
                  },
                  {
                    path: DYNAMIC_ROUTES.ADMIN_CARE_CIRCLE_VIEW(
                      ':careCircleID',
                    ),
                    element: <AdminCareCircleView />,
                    handle: { stack: true },
                  },
                  {
                    path: ROUTES.ADMIN_ACCOUNT_MANAGE,
                    element: <ManageAccounts />,
                  },
                  {
                    path: ROUTES.ADMIN_ACCOUNT_NEW,
                    element: <NewCareCircle />,
                    handle: { stack: true },
                  },
                  {
                    path: DYNAMIC_ROUTES.ADMIN_ACCOUNT_VERIFY(':accountID'),
                    element: <VerifyAccount />,
                    handle: { stack: true },
                  },
                  {
                    path: ROUTES.ADMIN_INVITE_MANAGE,
                    element: <ManageInvitedMembers />,
                  },
                  {
                    path: ROUTES.ADMIN_ORGANIZATION_MANAGE,
                    element: <OrganizationTable />,
                  },
                  {
                    path: ROUTES.ADMIN_ORGANIZATION_NEW,
                    element: <OrganizationEditor />,
                    handle: { stack: true },
                  },
                  {
                    path: DYNAMIC_ROUTES.ADMIN_ORGANIZATION_EDIT(
                      ':organizationID',
                    ),
                    element: <OrganizationEditor />,
                    handle: { stack: true },
                  },
                  {
                    path: DYNAMIC_ROUTES.ADMIN_ORGANIZATION_MEMBER(
                      ':organizationID',
                    ),
                    element: <OrganizationMemberTable />,
                    handle: { stack: true },
                  },
                  {
                    path: ROUTES.ADMIN_REFERRING_PARTNER_MANAGE,
                    element: <ReferringPartnerTable />,
                  },
                  {
                    path: ROUTES.ADMIN_REFERRING_PARTNER_NEW,
                    element: <ReferringPartnerEditor />,
                    handle: { stack: true },
                  },
                  {
                    path: DYNAMIC_ROUTES.ADMIN_REFERRING_PARTNER_EDIT(
                      ':partnerID',
                    ),
                    element: <ReferringPartnerEditor />,
                    handle: { stack: true },
                  },
                  {
                    path: ROUTES.ADMIN_PARTNER_PRODUCT_MANAGE,
                    element: <PartnerProductTable />,
                  },
                  {
                    path: ROUTES.ADMIN_SERVICE_ACCOUNT,
                    element: <ServiceAccountTable />,
                  },
                  {
                    path: DYNAMIC_ROUTES.ADMIN_CALL_NOTE_NEW_FOR_CIRCLE(
                      ':careCircleID',
                    ),
                    element: <CallNoteEditor />,
                    handle: { stack: true },
                  },
                  {
                    path: DYNAMIC_ROUTES.ADMIN_CALL_NOTES(':careCircleID'),
                    element: <AdminCallNotes />,
                    handle: { stack: true },
                  },
                  {
                    path: DYNAMIC_ROUTES.ADMIN_CALL_NOTE_VIEW(
                      ':careCircleID',
                      ':documentID',
                    ),
                    element: <CallNoteView />,
                    handle: { stack: true },
                  },
                  {
                    path: DYNAMIC_ROUTES.ADMIN_CALL_NOTE_EDIT(
                      ':careCircleID',
                      ':documentID',
                    ),
                    element: <CallNoteEditor />,
                    handle: { stack: true },
                  },
                  {
                    element: <FeatureGate feature={Feature.BACKTESTING} />,
                    children: [
                      {
                        path: ROUTES.ADMIN_BACKTESTS,
                        element: <AdminBacktests />,
                      },
                      {
                        path: ROUTES.ADMIN_BACKTEST_NEW,
                        element: <AdminBacktestNew />,
                        handle: { stack: true },
                      },
                      {
                        path: DYNAMIC_ROUTES.ADMIN_BACKTEST(':backtestID'),
                        element: <AdminBacktest />,
                        handle: { stack: true },
                      },
                    ],
                  },
                ],
              },

              // member pages (IndexGate handles auth/verify/onboard, RootChrome handles chrome + stack overlay)
              {
                element: <IndexGate />,
                children: [
                  {
                    element: <RootChrome />,
                    handle: { layout: 'member' },
                    children: [
                      // chrome pages (shown inline with NavDrawer)
                      {
                        index: true,
                        element: <Dashboard />,
                      },
                      {
                        path: ROUTES.ORGANISER,
                        element: <Organiser />,
                      },
                      {
                        path: ROUTES.V2_ACTIONS,
                        element: <MemberActions />,
                      },
                      {
                        path: DYNAMIC_ROUTES.V2_ACTIONS(
                          ':careCircleId',
                          ':calendarId',
                        ),
                        element: <MemberActions />,
                      },
                      {
                        path: ROUTES.ACTION_PLAN,
                        element: <ActionPlan />,
                      },
                      {
                        path: ROUTES.CARE_PROFILE,
                        element: <CareProfile />,
                      },
                      {
                        path: ROUTES.SERVICE,
                        element: <ServiceOverview />,
                      },
                      {
                        path: ROUTES.WEBINAR,
                        element: <WebinarOverview />,
                      },
                      {
                        path: ROUTES.DIRECTORY,
                        element: <Directory />,
                      },
                      {
                        path: ROUTES.CARE_SEARCH,
                        element: <CareSearchGate />,
                      },
                      {
                        path: ROUTES.KEY_INFORMATION,
                        element: <KeyInformation />,
                      },
                      {
                        path: ROUTES.MESSENGER,
                        element: <Dashboard />,
                      },

                      // stacked pages (full-screen overlay with slide animation)
                      {
                        handle: { stack: true },
                        children: [
                          // shortlist detail
                          {
                            path: DYNAMIC_ROUTES.SHORTLIST_DETAIL(
                              ':shortlistID',
                            ),
                            element: <ShortlistDetail />,
                          },

                          // power of attorney
                          {
                            element: <FeatureGate feature={Feature.POA} />,
                            children: [
                              {
                                path: ROUTES.POWER_OF_ATTORNEY,
                                element: <Poa screen='home' />,
                              },
                              {
                                path: ROUTES.POA_PRICING,
                                element: <Poa screen='pricing' />,
                              },
                              {
                                path: ROUTES.POA_SUCCESS,
                                element: <PoaSuccess />,
                              },
                              {
                                path: ROUTES.ZENCO_REDIRECT,
                                element: (
                                  <Navigate
                                    to={ROUTES.POWER_OF_ATTORNEY}
                                    replace
                                  />
                                ),
                              },
                            ],
                          },

                          // sandbox
                          {
                            element: (
                              <FeatureGate
                                feature={Feature.DYNAMIC_CHAT_SANDBOX}
                              />
                            ),
                            children: [
                              {
                                path: ROUTES.SANDBOX,
                                element: <Sandbox />,
                              },
                              {
                                path: DYNAMIC_ROUTES.SANDBOX_CHAT(
                                  ':mode',
                                  ':chatSessionID',
                                ),
                                element: <ServerChat />,
                              },
                            ],
                          },

                          // action plan
                          {
                            path: DYNAMIC_ROUTES.ACTION_PLAN_CATEGORY(
                              ':actionPlanCategoryId',
                            ),
                            element: <ActionPlanCategory />,
                          },
                          {
                            path: DYNAMIC_ROUTES.ACTION_PLAN_ITEM(
                              ':actionPlanItemId',
                            ),
                            element: <ActionPlanContent />,
                          },

                          // articles
                          {
                            path: ROUTES.LIBRARY,
                            element: <ArticleOverview />,
                          },
                          {
                            path: ROUTES.LIBRARY_BOOKMARKS,
                            element: <ArticleBookmarks />,
                          },
                          {
                            path: DYNAMIC_ROUTES.LIBRARY_ARTICLE(':articleId'),
                            element: <ArticleContent />,
                          },

                          // care circle
                          {
                            path: ROUTES.CARE_CIRCLE,
                            element: <CareCircle />,
                          },
                          {
                            path: ROUTES.CARE_CIRCLE_NEW,
                            element: <CareCircleCreate />,
                          },
                          {
                            path: ROUTES.CARE_CIRCLE_INVITE,
                            element: <CareCircleInvite />,
                          },
                          {
                            path: DYNAMIC_ROUTES.CARE_CIRCLE_MEMBER(
                              ':accountID',
                            ),
                            element: <CareCircleMemberInfo />,
                          },

                          // key documents
                          {
                            path: ROUTES.KEY_DOCUMENTS,
                            element: <KeyDocuments />,
                          },
                          {
                            path: ROUTES.DOCUMENT_UPLOAD,
                            element: <DocumentUpload />,
                          },
                          {
                            path: ROUTES.AUDIO_RECORD,
                            element: <AudioRecord />,
                          },
                          {
                            path: ROUTES.CALLS,
                            element: <CallsList />,
                          },
                          {
                            path: DYNAMIC_ROUTES.CALL_NOTE_VIEW(':documentID'),
                            element: <CallSummary />,
                          },

                          // care profile
                          {
                            path: ROUTES.CARE_PROFILE_START,
                            element: <CareProfileStart />,
                          },

                          // care assessment
                          {
                            element: (
                              <FeatureGate feature={Feature.CARE_ASSESSMENT} />
                            ),
                            children: [
                              {
                                path: ROUTES.CARE_ASSESSMENT,
                                element: <CarePlan />,
                              },
                              {
                                path: DYNAMIC_ROUTES.CARE_ASSESSMENT_FORM(
                                  ':careProfileFormId',
                                ),
                                element: <CareProfileForm />,
                              },
                              {
                                path: ROUTES.CARE_ASSESSMENT_COMPLETE,
                                element: <CareProfileComplete />,
                              },
                            ],
                          },

                          // attendance allowance
                          {
                            element: (
                              <FeatureGate
                                feature={Feature.ATTENDANCE_ALLOWANCE}
                                fallbackRedirect={DYNAMIC_ROUTES.LIBRARY_ARTICLE(
                                  'complete-guide-to-attendance-allowance',
                                )}
                              />
                            ),
                            children: [
                              {
                                path: ROUTES.ATTENDANCE_ALLOWANCE,
                                element: <AttendanceAllowance />,
                              },
                              {
                                path: ROUTES.ATTENDANCE_ALLOWANCE_START,
                                element: <AttendanceAllowance />,
                              },
                              {
                                path: DYNAMIC_ROUTES.ATTENDANCE_ALLOWANCE_FORM(
                                  ':careProfileFormId',
                                ),
                                element: <AttendanceAllowanceForm />,
                              },
                              {
                                path: ROUTES.ATTENDANCE_ALLOWANCE_COMPLETE,
                                element: <AttendanceAllowanceComplete />,
                              },
                              {
                                path: ROUTES.ATTENDANCE_ALLOWANCE_SUCCESS,
                                element: <AttendanceAllowanceSuccess />,
                              },
                            ],
                          },

                          // financial status
                          {
                            element: (
                              <FeatureGate feature={Feature.FINANCIAL_STATUS} />
                            ),
                            children: [
                              {
                                path: ROUTES.FINANCIAL_STATUS,
                                element: <FinancialStatusStart />,
                              },
                              {
                                path: DYNAMIC_ROUTES.FINANCIAL_STATUS_FORM(
                                  ':careProfileFormId',
                                ),
                                element: <FinancialStatusForm />,
                              },
                              {
                                path: ROUTES.FINANCIAL_STATUS_COMPLETE,
                                element: <FinancialStatusComplete />,
                              },
                            ],
                          },

                          // benefits eligibility v1
                          {
                            element: <EligibilityCalculatorGate version='v1' />,
                            children: [
                              {
                                path: ROUTES.ELIGIBILITY_CALCULATOR,
                                element: <BenefitsEligibilityStart />,
                              },
                              {
                                path: DYNAMIC_ROUTES.ELIGIBILITY_CALCULATOR_FORM(
                                  ':careProfileFormId',
                                ),
                                element: <BenefitsEligibilityForm />,
                              },
                              {
                                path: ROUTES.ELIGIBILITY_CALCULATOR_COMPLETE,
                                element: <BenefitsEligibilityComplete />,
                              },
                            ],
                          },

                          // benefits eligibility v2
                          {
                            element: <EligibilityCalculatorGate version='v2' />,
                            children: [
                              {
                                path: ROUTES.ELIGIBILITY_CALCULATOR_V2,
                                element: <BenefitsEligibilityV2Start />,
                              },
                              {
                                // Both optional: without a care circle it redirects to the active one,
                                // and without a session the latest one is picked up.
                                path: DYNAMIC_ROUTES.ELIGIBILITY_CALCULATOR_V2_FORM(
                                  ':careCircleId?',
                                  ':chatSessionId?',
                                ),
                                element: <BenefitsEligibilityV2Form />,
                              },
                              {
                                path: ROUTES.ELIGIBILITY_CALCULATOR_V2_COMPLETE,
                                element: <BenefitsEligibilityV2Complete />,
                              },
                            ],
                          },

                          // services detail
                          {
                            path: DYNAMIC_ROUTES.SERVICE_DETAIL(':serviceId'),
                            element: <Service />,
                          },

                          // webinar detail
                          {
                            path: DYNAMIC_ROUTES.WEBINAR_DETAIL(':webinarId'),
                            element: <WebinarContent />,
                          },

                          // directory detail
                          {
                            path: DYNAMIC_ROUTES.DIRECTORY_PROVIDER(
                              ':providerID',
                            ),
                            element: <DirectoryProfileView />,
                          },
                          {
                            path: ROUTES.DIRECTORY_CARE_HOME,
                            element: <OnboardingDirectoryCareHome />,
                          },
                          {
                            path: ROUTES.DIRECTORY_AT_HOME,
                            element: <OnboardingDirectoryAtHome />,
                          },

                          // book call
                          {
                            path: ROUTES.BOOK_CALL,
                            element: <BookCallGate />,
                          },
                          {
                            element: (
                              <PermissionGate
                                permission={Permission.BOOK_CALL}
                              />
                            ),
                            children: [
                              {
                                path: ROUTES.BOOK_CALL_DETAILS,
                                element: <BookCallDetails />,
                              },
                            ],
                          },

                          // care circle alt
                          {
                            element: (
                              <FeatureGate feature={Feature.CARE_CIRCLE} />
                            ),
                            children: [
                              {
                                path: ROUTES.CIRCLE,
                                element: <CareCircle />,
                              },
                            ],
                          },

                          // care circle undefined redirects
                          {
                            path: ROUTES.CARE_CIRCLE + '/undefined',
                            element: <Navigate to={ROUTES.HOME} />,
                          },
                          {
                            path: ROUTES.CARE_CIRCLE + '/undefined/:wildcard',
                            element: <Navigate to={ROUTES.HOME} />,
                          },
                        ],
                      },
                    ],
                  },
                ],
              },
            ],
          },

          // catch-all
          {
            path: wildcard(),
            element: <Navigate to={ROUTES.HOME} replace />,
          },
        ],
      },
    ],
    [requireAuth, requireOnboarded, requireRoles, requireVerified, buildLoader],
  );

  const routerRef = useRef<ReturnType<typeof createBrowserRouter> | null>(null);

  if (!routerRef.current) {
    routerRef.current = createBrowserRouter(pruneRoutes(routes));
  }

  const router = routerRef.current;

  return router;
};

export const Router = () => {
  const router = useRouter();
  return <RouterProvider router={router} />;
};
